import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MessageCircle, Smile, Heart, Award, Trash2, SendHorizontal, ChevronDown, ChevronUp, Search, Pencil, Image, Video, X, Waves, Eye } from 'lucide-react';
import { Avatar, Button, Modal } from '../../components/UI';
import { appreciationsApi, analyticsApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Appreciation, Comment, CompanyValue } from '../../types';
import { renderValueIcon } from '../../utils/iconMap';
import { Link, useSearchParams } from 'react-router-dom';
import MentionInput from '../../components/MentionInput/MentionInput';
import MentionText from '../../components/MentionText/MentionText';
import styles from './Feed.module.css';

type TimePeriod = '7' | '30' | '90' | 'custom';

const RIPPLE_TYPES: Record<string, { label: string; emoji: string; color: string; bg: string; border: string }> = {
  kudos: { label: 'Kudos', emoji: '👏', color: '#8B5CF6', bg: '#F5F3FF', border: '#DDD6FE' },
  shoutout: { label: 'Shoutout', emoji: '📣', color: '#EC4899', bg: '#FDF2F8', border: '#FBCFE8' },
  thank_you: { label: 'Thank You', emoji: '🙏', color: '#6366F1', bg: '#EEF2FF', border: '#C7D2FE' },
  great_job: { label: 'Great Job', emoji: '🏆', color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  team_player: { label: 'Team Player', emoji: '🤝', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  innovator: { label: 'Innovator', emoji: '💡', color: '#0EA5E9', bg: '#F0F9FF', border: '#BAE6FD' },
  customer_hero: { label: 'Customer Hero', emoji: '❤️', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
};

const FILTER_CLASS_MAP: Record<string, string> = {
  kudos: 'activeKudos',
  shoutout: 'activeShoutout',
  thank_you: 'activeThankYou',
  great_job: 'activeGreatJob',
  team_player: 'activeTeamPlayer',
  innovator: 'activeInnovator',
  customer_hero: 'activeCustomerHero',
};

const reactions = ['👍', '👏', '❤️', '🎉', '💡', '🔥', '⭐', '😊'];

const Feed: React.FC = () => {
  const { user, refreshUser } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [appreciations, setAppreciations] = useState<Appreciation[]>([]);
  const [loading, setLoading] = useState(true);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const highlightWidenTimeRef = useRef(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAppreciation, setEditingAppreciation] = useState<Appreciation | null>(null);
  const [editForm, setEditForm] = useState({
    message: '',
    type: '',
    is_public: true,
    media_url: '' as string | null,
    media_type: null as 'image' | 'video' | null,
    company_value_id: '' as string | null,
    media_files: [] as { url: string; type: string; name?: string }[],
  });
  const [saving, setSaving] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [companyValues, setCompanyValues] = useState<CompanyValue[]>([]);

  // Viewed-by modal state
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [viewersData, setViewersData] = useState<any[]>([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [viewersTitle, setViewersTitle] = useState('');

  // Reactions modal state
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [reactionsModalData, setReactionsModalData] = useState<any[]>([]);

  // Add-On state
  const [addonOpenId, setAddonOpenId] = useState<string | null>(null);
  const [addonPoints, setAddonPoints] = useState(10);
  const [addonMessage, setAddonMessage] = useState('');
  const [addonSubmitting, setAddonSubmitting] = useState(false);
  const [addonsData, setAddonsData] = useState<Record<string, any[]>>({});

  const canViewViewers = user?.role === 'superadmin';

  const handleViewViewers = useCallback(async (e: React.MouseEvent, appreciation: Appreciation) => {
    e.stopPropagation();
    if (!canViewViewers) return;
    setViewersTitle(`${appreciation.from_first_name}'s ripple to ${appreciation.to_first_name}`);
    setShowViewersModal(true);
    setViewersLoading(true);
    try {
      const response = await appreciationsApi.getViewers(appreciation.id);
      setViewersData(response.data);
    } catch (error) {
      console.error('Failed to fetch viewers:', error);
      setViewersData([]);
    } finally {
      setViewersLoading(false);
    }
  }, [canViewViewers]);

  const handleSubmitAddon = async (appreciationId: string) => {
    if (addonSubmitting || addonPoints < 5 || addonPoints > 100) return;
    setAddonSubmitting(true);
    try {
      const res = await appreciationsApi.addOn(appreciationId, { points: addonPoints, message: addonMessage.trim() || undefined });
      setAddonsData(prev => ({ ...prev, [appreciationId]: [...(prev[appreciationId] || []), res.data] }));
      setAddonOpenId(null);
      setAddonPoints(10);
      setAddonMessage('');
      refreshUser();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to add on');
    } finally {
      setAddonSubmitting(false);
    }
  };

  const fetchAddons = async (appreciationId: string) => {
    if (addonsData[appreciationId]) return;
    try {
      const res = await appreciationsApi.getAddons(appreciationId);
      setAddonsData(prev => ({ ...prev, [appreciationId]: res.data }));
    } catch { /* ignore */ }
  };

  // Time filter and search state
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDates, setCustomDates] = useState({ start: '', end: '' });

  // Chat/Comments state
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [commentsData, setCommentsData] = useState<Record<string, Comment[]>>({});
  const [commentsLoading, setCommentsLoading] = useState<Set<string>>(new Set());
  const [newComment, setNewComment] = useState<Record<string, string>>({});
  const [submittingComment, setSubmittingComment] = useState<Set<string>>(new Set());
  const [deletingComment, setDeletingComment] = useState<Set<string>>(new Set());
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [hoverReactFor, setHoverReactFor] = useState<string | null>(null);
  const [hoverReactPos, setHoverReactPos] = useState<{ top: number; left: number } | null>(null);
  const hoverReactTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [replyText, setReplyText] = useState('');
  const commentInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAppreciations(1, true);
    fetchCompanyValues();
  }, [filter, timePeriod]);

  // Handle highlight from notification click; widen the time filter if the
  // item isn't in the current window
  useEffect(() => {
    const id = searchParams.get('highlight');
    if (!id || loading) return;
    if (appreciations.some(a => a.id === id)) {
      setHighlightId(id);
      // Open the comments section so a comment notification shows the comment
      setExpandedComments(prev => new Set(prev).add(id));
      if (!commentsData[id]) fetchComments(id);
      setTimeout(() => {
        const el = document.querySelector(`[data-appreciation-id="${id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      setSearchParams({}, { replace: true });
      setTimeout(() => setHighlightId(null), 3000);
    } else if (timePeriod === '7' || timePeriod === '30') {
      highlightWidenTimeRef.current = Date.now();
      setTimePeriod('90');
    } else if (Date.now() - highlightWidenTimeRef.current > 1000) {
      // Widened window has finished loading and the item still isn't there — give up
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appreciations, loading, searchParams, setSearchParams, timePeriod]);

  const fetchCompanyValues = async () => {
    try {
      const response = await appreciationsApi.getValues();
      setCompanyValues(response.data);
    } catch (error) {
      console.error('Failed to fetch company values:', error);
    }
  };

  const fetchAppreciations = async (pageNum: number, reset = false) => {
    try {
      setLoading(true);
      const params: any = { page: pageNum };
      if (filter !== 'all') params.type = filter;
      if (timePeriod !== 'custom') {
        params.days = parseInt(timePeriod);
      } else if (customDates.start && customDates.end) {
        params.start_date = customDates.start;
        params.end_date = customDates.end;
      }
      const response = await appreciationsApi.getFeed(params);
      const newAppreciations = response.data.appreciations;
      if (reset) {
        setAppreciations(newAppreciations);
      } else {
        setAppreciations((prev) => [...prev, ...newAppreciations]);
      }
      setHasMore(pageNum < response.data.pagination.totalPages);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch appreciations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Track views using IntersectionObserver
  const viewedIdsRef = useRef<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-appreciation-id');
            if (id && !viewedIdsRef.current.has(id)) {
              viewedIdsRef.current.add(id);
              appreciationsApi.markViewed(id).then(() => {
                setAppreciations(prev => prev.map(a =>
                  a.id === id ? { ...a, view_count: (a.view_count || 0) + 1 } : a
                ));
              }).catch(() => {});
            }
          }
        });
      },
      { threshold: 0.5 }
    );
    return () => { observerRef.current?.disconnect(); };
  }, []);

  // Callback ref for observing feed cards
  const observeCard = useCallback((node: HTMLDivElement | null) => {
    if (node && observerRef.current) {
      observerRef.current.observe(node);
    }
  }, []);

  // Computed stats
  const totalRipples = appreciations.length;
  const totalWaves = useMemo(() => appreciations.reduce((s, a) => s + (a.points || 0), 0), [appreciations]);
  const topCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    appreciations.forEach(a => { counts[a.type] = (counts[a.type] || 0) + 1; });
    let best = { type: '', count: 0 };
    Object.entries(counts).forEach(([type, count]) => {
      if (count > best.count) best = { type, count };
    });
    return best;
  }, [appreciations]);

  // Filter appreciations by search term (client-side)
  const filteredAppreciations = appreciations.filter(a => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return (
      a.from_first_name?.toLowerCase().includes(s) ||
      a.from_last_name?.toLowerCase().includes(s) ||
      a.to_first_name?.toLowerCase().includes(s) ||
      a.to_last_name?.toLowerCase().includes(s) ||
      a.message?.toLowerCase().includes(s) ||
      a.type?.toLowerCase().includes(s)
    );
  });

  // Leaderboard
  // Leaderboard from API (consistent with Leaderboard tab)
  const [leaderboard, setLeaderboard] = useState<{ name: string; initials: string; waves: number; avatar_url?: string }[]>([]);
  useEffect(() => {
    const period = timePeriod === '7' ? 'week' : timePeriod === '90' ? 'quarter' : 'month';
    analyticsApi.getLeaderboard({ period, type: 'received' }).then(res => {
      const entries = res.data?.entries || res.data || [];
      setLeaderboard(entries.slice(0, 5).map((p: any) => ({
        name: `${p.first_name} ${p.last_name}`,
        initials: `${(p.first_name || '')[0]}${(p.last_name || '')[0]}`.toUpperCase(),
        waves: Number(p.points_received) || 0,
        avatar_url: p.avatar_url,
      })));
    }).catch(() => {});
  }, [timePeriod]);

  // Recognition breakdown
  const breakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    appreciations.forEach(a => { counts[a.type] = (counts[a.type] || 0) + 1; });
    return Object.entries(RIPPLE_TYPES).map(([key, t]) => ({
      key,
      ...t,
      count: counts[key] || 0,
      pct: appreciations.length > 0 ? Math.round(((counts[key] || 0) / appreciations.length) * 100) : 0,
    }));
  }, [appreciations]);

  const handleTimePeriodChange = (period: TimePeriod) => {
    if (period === 'custom') {
      setShowCustomDatePicker(true);
    } else {
      setTimePeriod(period);
      setShowCustomDatePicker(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customDates.start && customDates.end) {
      setTimePeriod('custom');
      setShowCustomDatePicker(false);
      fetchAppreciations(1, true);
    }
  };

  const fetchComments = async (appreciationId: string) => {
    setCommentsLoading(prev => new Set(prev).add(appreciationId));
    try {
      const response = await appreciationsApi.getComments(appreciationId);
      setCommentsData(prev => ({ ...prev, [appreciationId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setCommentsLoading(prev => { const next = new Set(prev); next.delete(appreciationId); return next; });
    }
  };

  const toggleComments = async (e: React.MouseEvent, appreciationId: string) => {
    e.stopPropagation();
    const isExpanded = expandedComments.has(appreciationId);
    if (isExpanded) {
      setExpandedComments(prev => { const next = new Set(prev); next.delete(appreciationId); return next; });
    } else {
      setExpandedComments(prev => new Set(prev).add(appreciationId));
      if (!commentsData[appreciationId]) await fetchComments(appreciationId);
      setTimeout(() => { commentInputRefs.current[appreciationId]?.focus(); }, 100);
    }
  };

  const handleSubmitComment = async (appreciationId: string) => {
    const content = newComment[appreciationId]?.trim();
    if (!content) return;
    setSubmittingComment(prev => new Set(prev).add(appreciationId));
    try {
      const response = await appreciationsApi.addComment(appreciationId, content);
      setCommentsData(prev => ({ ...prev, [appreciationId]: [...(prev[appreciationId] || []), response.data] }));
      setAppreciations(prev => prev.map(a => a.id === appreciationId ? { ...a, comment_count: (a.comment_count || 0) + 1 } : a));
      setNewComment(prev => ({ ...prev, [appreciationId]: '' }));
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(prev => { const next = new Set(prev); next.delete(appreciationId); return next; });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, appreciationId: string) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitComment(appreciationId); }
  };

  const handleDeleteComment = async (appreciationId: string, commentId: string) => {
    setDeletingComment(prev => new Set(prev).add(commentId));
    try {
      await appreciationsApi.deleteComment(appreciationId, commentId);
      setCommentsData(prev => ({ ...prev, [appreciationId]: prev[appreciationId]?.filter(c => c.id !== commentId) || [] }));
      setAppreciations(prev => prev.map(a => a.id === appreciationId ? { ...a, comment_count: Math.max(0, (a.comment_count || 1) - 1) } : a));
    } catch (error) {
      console.error('Failed to delete comment:', error);
    } finally {
      setDeletingComment(prev => { const next = new Set(prev); next.delete(commentId); return next; });
    }
  };

  const handleEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.content);
  };

  const handleSaveEditComment = async (appreciationId: string, commentId: string) => {
    if (!editCommentText.trim()) return;
    setSavingComment(true);
    try {
      const response = await appreciationsApi.editComment(appreciationId, commentId, editCommentText.trim());
      setCommentsData(prev => ({
        ...prev,
        [appreciationId]: prev[appreciationId]?.map(c =>
          c.id === commentId ? { ...c, content: response.data.content } : c
        ) || []
      }));
      setEditingCommentId(null);
      setEditCommentText('');
    } catch (error) {
      console.error('Failed to edit comment:', error);
    } finally {
      setSavingComment(false);
    }
  };

  const handleCommentReaction = async (appreciationId: string, commentId: string, emoji: string) => {
    try {
      await appreciationsApi.addCommentReaction(appreciationId, commentId, emoji);
      const response = await appreciationsApi.getComments(appreciationId);
      setCommentsData(prev => ({ ...prev, [appreciationId]: response.data }));
    } catch (err) { console.error(err); }
  };

  const handleReply = async (appreciationId: string, parentId: string) => {
    if (!replyText.trim()) return;
    try {
      const response = await appreciationsApi.addComment(appreciationId, replyText.trim(), parentId);
      setCommentsData(prev => ({ ...prev, [appreciationId]: [...(prev[appreciationId] || []), response.data] }));
      setAppreciations(prev => prev.map(a => a.id === appreciationId ? { ...a, comment_count: (a.comment_count || 0) + 1 } : a));
      setReplyingToId(null);
      setReplyText('');
    } catch (err) { console.error(err); }
  };

  const handleReaction = async (id: string, emoji: string) => {
    try {
      const response = await appreciationsApi.addReaction(id, emoji);
      const { action, previousEmoji } = response.data;
      setAppreciations((prev) =>
        prev.map((a) => {
          if (a.id !== id) return a;
          let reactions = [...(a.reactions || [])];
          if (action === 'removed') {
            reactions = reactions.map(r => r.emoji === emoji ? { ...r, count: r.count - 1 } : r).filter(r => r.count > 0);
          } else if (action === 'replaced') {
            reactions = reactions.map(r => r.emoji === previousEmoji ? { ...r, count: r.count - 1 } : r).filter(r => r.count > 0);
            const existing = reactions.find(r => r.emoji === emoji);
            if (existing) {
              reactions = reactions.map(r => r.emoji === emoji ? { ...r, count: r.count + 1 } : r);
            } else {
              reactions.push({ emoji, count: 1 });
            }
          } else {
            const existing = reactions.find(r => r.emoji === emoji);
            if (existing) {
              reactions = reactions.map(r => r.emoji === emoji ? { ...r, count: r.count + 1 } : r);
            } else {
              reactions.push({ emoji, count: 1 });
            }
          }
          return { ...a, reactions };
        })
      );
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCommentDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleDeleteClick = (id: string) => { setDeleteId(id); setDeleteModalOpen(true); };

  const handleEditClick = (appreciation: Appreciation) => {
    setEditingAppreciation(appreciation);
    const companyValue = companyValues.find(cv => cv.name === appreciation.company_value);
    const existingMedia = appreciation.media_files && appreciation.media_files.length > 0
      ? appreciation.media_files
      : appreciation.media_url ? [{ url: appreciation.media_url, type: appreciation.media_type || 'image', name: undefined }] : [];
    setEditForm({
      message: appreciation.message,
      type: appreciation.type,
      is_public: appreciation.is_public !== false,
      media_url: appreciation.media_url || null,
      media_type: appreciation.media_type || null,
      company_value_id: companyValue?.id || null,
      media_files: existingMedia as any[],
    });
    setEditModalOpen(true);
  };

  const handleEditMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { alert('File size must be less than 5MB'); return; }
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const fileType = isImage ? 'image' : isVideo ? 'video' : 'file';
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditForm(prev => ({
          ...prev,
          media_files: [...prev.media_files, { url: reader.result as string, type: fileType, name: file.name }],
          media_url: prev.media_url || reader.result as string,
          media_type: prev.media_type || (isImage ? 'image' : 'video') as 'image' | 'video',
        }));
      };
      reader.readAsDataURL(file);
    });
    if (e.target) e.target.value = '';
  };

  const handleRemoveEditMedia = (index?: number) => {
    if (index !== undefined) {
      setEditForm(prev => {
        const newFiles = prev.media_files.filter((_, i) => i !== index);
        return {
          ...prev,
          media_files: newFiles,
          media_url: newFiles.length > 0 ? newFiles[0].url : null,
          media_type: newFiles.length > 0 ? newFiles[0].type as 'image' | 'video' : null,
        };
      });
    } else {
      setEditForm(prev => ({ ...prev, media_url: null, media_type: null, media_files: [] }));
    }
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  };

  const handleEditSave = async () => {
    if (!editingAppreciation) return;
    setSaving(true);
    try {
      const response = await appreciationsApi.update(editingAppreciation.id, {
        message: editForm.message, type: editForm.type, is_public: editForm.is_public,
        media_url: editForm.media_files.length > 0 ? editForm.media_files[0].url : editForm.media_url,
        media_type: editForm.media_files.length > 0 ? editForm.media_files[0].type as 'image' | 'video' : editForm.media_type,
        media_files: editForm.media_files.length > 0 ? editForm.media_files : undefined,
        company_value_id: editForm.company_value_id,
      });
      setAppreciations(prev => prev.map(a => a.id === editingAppreciation.id ? { ...a, ...response.data } : a));
      setEditModalOpen(false);
      setEditingAppreciation(null);
    } catch (error) {
      console.error('Failed to update appreciation:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await appreciationsApi.delete(deleteId);
      setAppreciations(prev => prev.filter(a => a.id !== deleteId));
      setDeleteModalOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error('Failed to delete appreciation:', error);
    } finally {
      setDeleting(false);
    }
  };

  const getFilterBtnClass = (key: string) => {
    if (filter !== key) return styles.filterBtn;
    if (key === 'all') return `${styles.filterBtn} ${styles.active}`;
    const activeClass = FILTER_CLASS_MAP[key];
    return `${styles.filterBtn} ${activeClass ? styles[activeClass] : styles.active}`;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.pageTitle}>Spotlight</h1>
          <p className={styles.pageSubtitle}>Everything Matasma has been grateful for lately.</p>
        </div>
        <div className={styles.timeFilterGroup}>
          {(['7', '30', '90'] as TimePeriod[]).map(p => (
            <button
              key={p}
              className={`${styles.timeFilterBtn} ${timePeriod === p ? styles.active : ''}`}
              onClick={() => handleTimePeriodChange(p)}
            >
              {p === '7' ? '7 Days' : p === '30' ? '30 Days' : '90 Days'}
            </button>
          ))}
          <button
            className={`${styles.timeFilterBtn} ${timePeriod === 'custom' || showCustomDatePicker ? styles.active : ''}`}
            onClick={() => handleTimePeriodChange('custom')}
          >
            Custom
          </button>
        </div>
      </div>


      {/* Main Grid */}
      <div className={styles.mainGrid}>
        {/* Left: Feed */}
        <div>
          {/* Search */}
          <div className={styles.filtersRow}>
            <div className={styles.searchWrapper}>
              <Search size={16} className={styles.searchIconEl} />
              <input
                type="text"
                placeholder="Search by name or message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button className={styles.clearSearch} onClick={() => setSearchTerm('')}>×</button>
              )}
            </div>
          </div>

          {showCustomDatePicker && (
            <div className={styles.customDatePicker}>
              <input type="date" value={customDates.start} onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })} className={styles.dateInput} />
              <span>to</span>
              <input type="date" value={customDates.end} onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })} className={styles.dateInput} />
              <Button size="sm" onClick={handleCustomDateApply}>Apply</Button>
            </div>
          )}

          {/* Type Filters */}
          <div className={styles.typeFilters}>
            <button className={getFilterBtnClass('all')} onClick={() => setFilter('all')}>All</button>
            {Object.entries(RIPPLE_TYPES).map(([key, t]) => (
              <button
                key={key}
                className={getFilterBtnClass(key)}
                onClick={() => setFilter(filter === key ? 'all' : key)}
              >
                {t.emoji} {t.label}
              </button>
            ))}
          </div>

          {/* Section Divider */}
          <div className={styles.sectionDivider}>
            <div className={styles.sectionDividerLine} />
            <span className={styles.sectionDividerLabel}>
              {timePeriod === '7' ? 'THIS WEEK' : timePeriod === '30' ? 'THIS MONTH' : timePeriod === '90' ? 'LAST 90 DAYS' : 'RESULTS'}
            </span>
            <div className={styles.sectionDividerLine} />
          </div>

          {/* Feed Cards */}
          <div className={styles.feed}>
            {filteredAppreciations.length === 0 && !loading && (
              <div className={styles.emptyState}>
                <div style={{ fontSize: 38 }}>🔍</div>
                <h3>No ripples match your filters</h3>
                <p>Try adjusting your filters or time period</p>
              </div>
            )}

            {filteredAppreciations.map((appreciation) => {
              const typeInfo = RIPPLE_TYPES[appreciation.type] || { label: appreciation.type, emoji: '👏', color: '#8B5CF6', bg: '#F5F3FF', border: '#DDD6FE' };
              const isCommentsExpanded = expandedComments.has(appreciation.id);
              const comments = commentsData[appreciation.id] || [];
              const isLoadingComments = commentsLoading.has(appreciation.id);
              const isSubmitting = submittingComment.has(appreciation.id);

              const recipients = (appreciation.recipients && appreciation.recipients.length > 1)
                ? appreciation.recipients
                : [{ to_user_id: appreciation.to_user_id, to_first_name: appreciation.to_first_name, to_last_name: appreciation.to_last_name, to_avatar: appreciation.to_avatar }];
              const recipientNames = recipients.map(r => r.to_first_name).join(' & ');

              return (
                <div key={appreciation.id} ref={observeCard} data-appreciation-id={appreciation.id}
                  className={styles.cardGlow}
                  style={{ background: `linear-gradient(135deg, ${typeInfo.color}1c, ${typeInfo.color}08 46%, #dff0ec99)` }}
                >
                  {/* ── Person Chip: sender LEFT, waves CENTER, recipients RIGHT ── */}
                  <div className={styles.personChip} style={{ background: `linear-gradient(100deg, #ffffffe6, ${typeInfo.color}1a)` }}>
                    <Avatar src={appreciation.from_avatar} name={`${appreciation.from_first_name} ${appreciation.from_last_name}`} size="md" />
                    <div className={styles.personChipInfo}>
                      <span className={styles.personChipName}>{appreciation.from_first_name} {appreciation.from_last_name}</span>
                      <span className={styles.personChipMeta}>recognised {recipientNames}</span>
                    </div>
                    <span className={styles.wavePill} style={{ color: typeInfo.color }}>+{appreciation.points || appreciation.total_points}</span>
                    <div className={styles.personChipAvatars}>
                      {recipients.slice(0, 3).map((r) => (
                        <Avatar key={r.to_user_id} src={r.to_avatar} name={`${r.to_first_name} ${r.to_last_name}`} size="md" />
                      ))}
                    </div>
                  </div>

                  <article className={`${styles.feedCard} ${highlightId === appreciation.id ? styles.highlighted : ''}`}>
                  {/* ── Colored Band ── */}
                  <div className={styles.cardBand} style={{ background: `linear-gradient(90deg, ${typeInfo.bg}, ${typeInfo.bg}80)`, borderBottomColor: `${typeInfo.color}2b` }}>
                    <span className={styles.bandTag} style={{ background: typeInfo.bg, color: typeInfo.color }}>{typeInfo.label.toUpperCase()}</span>
                    <div style={{ flex: 1 }} />
                    <span className={styles.bandTime}>{formatDate(appreciation.created_at)}</span>
                    {user?.id === appreciation.from_user_id && (
                      <button className={styles.metaActionBtn} onClick={() => handleEditClick(appreciation)} title="Edit">
                        <Pencil size={14} />
                      </button>
                    )}
                    {user?.role === 'superadmin' && (
                      <button className={`${styles.metaActionBtn} ${styles.metaActionDanger}`} onClick={() => handleDeleteClick(appreciation.id)} title="Delete">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {/* ── Message ── */}
                  <div className={styles.messageWrap}>
                    <p className={styles.message}>
                      <MentionText text={appreciation.message} />
                    </p>

                    {/* Media */}
                    {(() => {
                      const files = (appreciation.media_files && appreciation.media_files.length > 0)
                        ? appreciation.media_files
                        : (appreciation.media_url ? [{ url: appreciation.media_url, type: appreciation.media_type || 'image' as const }] : []);
                      if (files.length === 0) return null;
                      const mediaFiles = files.filter(f => f.type === 'image' || f.type === 'video');
                      const docFiles = files.filter(f => f.type !== 'image' && f.type !== 'video');
                      return (
                        <>
                          {mediaFiles.length === 1 && (
                            <div className={styles.mediaContainer}>
                              {mediaFiles[0].type === 'image' ? (
                                <img src={mediaFiles[0].url} alt="Appreciation media" className={styles.mediaImage} onClick={(e) => { e.stopPropagation(); setPreviewImage(mediaFiles[0].url); }} />
                              ) : (
                                <video src={mediaFiles[0].url} className={styles.mediaVideo} controls />
                              )}
                            </div>
                          )}
                          {mediaFiles.length > 1 && (
                            <div className={`${styles.mediaGrid} ${styles.mediaGrid2}`}>
                              {mediaFiles.map((mf, i) => (
                                <div key={i} className={styles.mediaGridItem}>
                                  {mf.type === 'image' ? <img src={mf.url} alt="Media" onClick={(e) => { e.stopPropagation(); setPreviewImage(mf.url); }} /> : <video src={mf.url} controls />}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}

                    {/* Company Value Tags */}
                    {appreciation.company_value && (
                      <div className={styles.valueTags}>
                        <div
                          className={styles.valueTag}
                          style={appreciation.company_value_color ? {
                            background: `${appreciation.company_value_color}14`,
                            color: appreciation.company_value_color,
                          } : undefined}
                        >
                          <span className={styles.valueIcon}>
                            {renderValueIcon(appreciation.company_value_icon, 14, appreciation.company_value_color || undefined)}
                          </span>
                          <span>{appreciation.company_value}</span>
                        </div>
                      </div>
                    )}

                    {/* Engagement Summary (reactions) — clickable to open modal */}
                    {appreciation.reactions && appreciation.reactions.length > 0 && (
                      <div className={styles.engagementSummary} style={{ cursor: 'pointer' }} onClick={(e) => {
                        e.stopPropagation();
                        const allUsers = appreciation.reactions?.flatMap((r: any) =>
                          (r.users || []).map((u: any) => ({ ...u, emoji: r.emoji }))
                        ) || [];
                        setReactionsModalData(allUsers);
                        setShowReactionsModal(true);
                      }}>
                        <div className={styles.reactionSummary}>
                          {appreciation.reactions.map((reaction) => (
                            <span key={reaction.emoji} className={styles.reactionChip}>
                              {reaction.emoji} {reaction.count}
                              {reaction.users && reaction.users.length > 0 && (
                                <div className={styles.reactionTooltip}>
                                  {reaction.users.slice(0, 10).map((u, i) => (
                                    <div key={i} className={styles.reactionUser}>{u.first_name} {u.last_name}</div>
                                  ))}
                                  {reaction.users.length > 10 && (
                                    <div className={styles.reactionUser}>+{reaction.users.length - 10} more</div>
                                  )}
                                </div>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Addons Section ── */}
                  {(addonsData[appreciation.id] || []).length > 0 && (
                    <div className={styles.addonsSection}>
                      {addonsData[appreciation.id].map((addon: any) => (
                        <div key={addon.id} className={styles.addonRow}>
                          <Avatar src={addon.avatar_url} name={`${addon.first_name} ${addon.last_name}`} size="xs" />
                          <span className={styles.addonText}>
                            <strong>{addon.first_name}</strong> added <span className={styles.addonWaves}>+{addon.points}</span>
                            {addon.message && ` — ${addon.message}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* ── Add-On Form (inline) ── */}
                  {addonOpenId === appreciation.id && (
                    <div className={styles.addonForm}>
                      <div className={styles.addonFormRow}>
                        <label className={styles.addonLabel}>Waves</label>
                        <input type="number" min={5} max={100} value={addonPoints} onChange={e => setAddonPoints(Number(e.target.value))} className={styles.addonInput} />
                      </div>
                      <div className={styles.addonFormRow}>
                        <label className={styles.addonLabel}>Message (optional)</label>
                        <input type="text" value={addonMessage} onChange={e => setAddonMessage(e.target.value)} placeholder="Add a note..." className={styles.addonInput} style={{ flex: 1 }} />
                      </div>
                      <div className={styles.addonFormActions}>
                        <button className={styles.addonCancel} onClick={() => setAddonOpenId(null)}>Cancel</button>
                        <button className={styles.addonSubmit} onClick={() => handleSubmitAddon(appreciation.id)} disabled={addonSubmitting || addonPoints < 5 || addonPoints > 100}>
                          {addonSubmitting ? 'Adding...' : `Add +${addonPoints} waves`}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── Footer ── */}
                  <div className={styles.cardFooter}>
                    <div className={styles.footerLeft}>
                      <button className={styles.footerBtn} onClick={(e) => toggleComments(e, appreciation.id)}>
                        Comment{appreciation.comment_count > 0 ? ` (${appreciation.comment_count})` : ''}
                      </button>
                      <button className={styles.footerBtn} onClick={() => { setAddonOpenId(addonOpenId === appreciation.id ? null : appreciation.id); fetchAddons(appreciation.id); }}>
                        Add on
                      </button>
                      {(() => {
                        const allFiles = (appreciation.media_files && appreciation.media_files.length > 0)
                          ? appreciation.media_files
                          : [];
                        return allFiles.filter((f: any) => f.type !== 'image' && f.type !== 'video').map((mf: any, i: number) => (
                          <a key={i} href={mf.url} download={mf.name || 'file'} className={styles.footerBtn} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none' }}>
                            Download file
                          </a>
                        ));
                      })()}
                    </div>
                    <div className={styles.footerRight}>
                      {(() => {
                        const totalReactions = appreciation.reactions?.reduce((sum: number, r: any) => sum + r.count, 0) || 0;
                        const allReactors = appreciation.reactions?.flatMap((r: any) => (r.users || []).map((u: any) => ({ ...u, emoji: r.emoji }))) || [];
                        if (totalReactions > 0) {
                          return (
                            <>
                              <div className={styles.footerReactorAvatars}>
                                {allReactors.slice(0, 3).map((reactor: any, i: number) => (
                                  <Avatar key={i} src={reactor.avatar_url} name={`${reactor.first_name} ${reactor.last_name}`} size="xs" />
                                ))}
                              </div>
                              <span
                                className={styles.footerReactions}
                                onClick={(e) => { e.stopPropagation(); setReactionsModalData(allReactors); setShowReactionsModal(true); }}
                                style={{ cursor: 'pointer' }}
                              >{totalReactions} reactions</span>
                            </>
                          );
                        }
                        return null;
                      })()}
                      <div className={styles.likeActionWrapper}>
                        <button className={styles.footerBtnLike} style={{ borderColor: `${typeInfo.color}55`, background: typeInfo.bg, color: typeInfo.color }}>
                          Like
                        </button>
                        <div className={styles.likeActionDropdown}>
                          {reactions.map((emoji) => (
                            <button key={emoji} className={styles.emojiOption} onClick={() => handleReaction(appreciation.id, emoji)}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ── Comments Section ── */}
                  {isCommentsExpanded && (
                    <div className={styles.commentsSection} onClick={(e) => e.stopPropagation()}>
                      {isLoadingComments ? (
                        <div className={styles.commentsLoading}>
                          <div className={styles.smallSpinner}></div>
                          <span>Loading comments...</span>
                        </div>
                      ) : (
                        <>
                          {comments.length > 0 && (() => {
                            const topLevelComments = comments.filter(c => !c.parent_id);
                            const getReplies = (parentId: string) => comments.filter(c => c.parent_id === parentId);

                            const renderComment = (comment: Comment, isReply = false) => (
                              <div key={comment.id} className={styles.commentItem}>
                                <Avatar src={comment.avatar_url} name={`${comment.first_name} ${comment.last_name}`} size="sm" />
                                <div className={styles.commentContent}>
                                  <div className={styles.commentHeader}>
                                    <span className={styles.commentAuthor}>{comment.first_name} {comment.last_name}</span>
                                    <span className={styles.commentTime}>{formatCommentDate(comment.created_at)}</span>
                                    {(user?.role === 'superadmin' || user?.id === comment.user_id) && editingCommentId !== comment.id && (
                                      <button className={styles.editCommentBtn} onClick={() => handleEditComment(comment)} title="Edit comment">
                                        <Pencil size={12} />
                                      </button>
                                    )}
                                    {user?.role === 'superadmin' && (
                                      <button className={styles.deleteCommentBtn} onClick={() => handleDeleteComment(appreciation.id, comment.id)} disabled={deletingComment.has(comment.id)} title="Delete comment">
                                        {deletingComment.has(comment.id) ? <div className={styles.tinySpinner}></div> : <Trash2 size={12} />}
                                      </button>
                                    )}
                                  </div>
                                  {editingCommentId === comment.id ? (
                                    <div className={styles.editCommentRow}>
                                      <MentionInput
                                        mode="input"
                                        value={editCommentText}
                                        onChange={setEditCommentText}
                                        onSubmit={() => handleSaveEditComment(appreciation.id, comment.id)}
                                        placeholder="Edit comment..."
                                        disabled={savingComment}
                                        className={styles.mentionInputWrap}
                                      />
                                      <button className={styles.saveEditBtn} onClick={() => handleSaveEditComment(appreciation.id, comment.id)} disabled={savingComment || !editCommentText.trim()}>
                                        {savingComment ? <div className={styles.tinySpinner}></div> : <SendHorizontal size={14} />}
                                      </button>
                                      <button className={styles.cancelEditBtn} onClick={() => setEditingCommentId(null)}>
                                        <X size={14} />
                                      </button>
                                    </div>
                                  ) : (
                                    <p className={styles.commentText}><MentionText text={comment.content} /></p>
                                  )}
                                  {/* Comment reactions */}
                                  {comment.reactions && comment.reactions.length > 0 && (
                                    <div className={styles.commentReactions}>
                                      {comment.reactions.map((r) => (
                                        <span
                                          key={r.emoji}
                                          className={styles.commentReactionChip}
                                          onClick={() => handleCommentReaction(appreciation.id, comment.id, r.emoji)}
                                        >
                                          {r.emoji} {r.count}
                                          {r.users && r.users.length > 0 && (
                                            <div className={styles.commentReactionTooltip}>
                                              {r.users.slice(0, 10).map((u, i) => (
                                                <div key={i}>{u.first_name} {u.last_name}</div>
                                              ))}
                                              {r.users.length > 10 && <div>+{r.users.length - 10} more</div>}
                                            </div>
                                          )}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {/* Action row */}
                                  {editingCommentId !== comment.id && (
                                    <div className={styles.commentActionRow}>
                                      <div
                                        className={styles.commentEmojiPicker}
                                        onMouseEnter={(e) => {
                                          if (hoverReactTimer.current) clearTimeout(hoverReactTimer.current);
                                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                          setHoverReactPos({ top: rect.top >= 50 ? rect.top - 42 : rect.bottom + 6, left: Math.max(8, Math.min(rect.left, window.innerWidth - 340)) });
                                          setHoverReactFor(comment.id);
                                        }}
                                        onMouseLeave={() => {
                                          hoverReactTimer.current = setTimeout(() => setHoverReactFor(null), 250);
                                        }}
                                      >
                                        <button className={styles.commentActionBtn}>
                                          <Smile size={12} /> React
                                        </button>
                                        {hoverReactFor === comment.id && hoverReactPos && (
                                          <div
                                            className={styles.commentEmojiDropdown}
                                            style={{ display: 'flex', position: 'fixed', top: hoverReactPos.top, left: hoverReactPos.left, bottom: 'auto' }}
                                          >
                                            {reactions.map((emoji) => (
                                              <button key={emoji} onClick={() => { handleCommentReaction(appreciation.id, comment.id, emoji); setHoverReactFor(null); }}>
                                                {emoji}
                                              </button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      {!isReply && (
                                        <button className={styles.commentActionBtn} onClick={() => { setReplyingToId(replyingToId === comment.id ? null : comment.id); setReplyText(''); }}>
                                          <MessageCircle size={12} /> Reply
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );

                            return (
                              <div className={styles.commentsList}>
                                {topLevelComments.map((comment) => (
                                  <div key={comment.id}>
                                    {renderComment(comment)}
                                    {/* Replies */}
                                    {getReplies(comment.id).length > 0 && (
                                      <div className={styles.replyGroup}>
                                        {getReplies(comment.id).map((reply) => renderComment(reply, true))}
                                      </div>
                                    )}
                                    {/* Reply input */}
                                    {replyingToId === comment.id && (
                                      <div className={styles.replyInputRow}>
                                        <div className={styles.commentInputContainer}>
                                          <MentionInput
                                          mode="input"
                                          placeholder={`Reply to ${comment.first_name}...`}
                                          value={replyText}
                                          onChange={setReplyText}
                                          onSubmit={() => handleReply(appreciation.id, comment.id)}
                                          className={styles.mentionInputWrap}
                                        />
                                          <button className={styles.sendCommentBtn} onClick={() => handleReply(appreciation.id, comment.id)} disabled={!replyText.trim()}>
                                          <SendHorizontal size={14} />
                                        </button>
                                        </div>
                                        <button className={styles.cancelEditBtn} onClick={() => { setReplyingToId(null); setReplyText(''); }}>
                                          <X size={14} />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                          {comments.length === 0 && (
                            <div className={styles.noComments}>
                              <MessageCircle size={20} />
                              <span>No comments yet. Be the first to comment!</span>
                            </div>
                          )}
                          <div className={styles.commentInputWrapper}>
                            <Avatar src={user?.avatar_url} name={`${user?.first_name} ${user?.last_name}`} size="sm" />
                            <div className={styles.commentInputContainer}>
                              <MentionInput
                                mode="input"
                                placeholder="Write a comment... Use @ to mention"
                                value={newComment[appreciation.id] || ''}
                                onChange={(val) => setNewComment(prev => ({ ...prev, [appreciation.id]: val }))}
                                onSubmit={() => handleSubmitComment(appreciation.id)}
                                disabled={isSubmitting}
                                className={styles.mentionInputWrap}
                              />
                              <button className={styles.sendCommentBtn} onClick={() => handleSubmitComment(appreciation.id)} disabled={!newComment[appreciation.id]?.trim() || isSubmitting}>
                                {isSubmitting ? <div className={styles.tinySpinner}></div> : <SendHorizontal size={16} />}
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </article>
                </div>
              );
            })}

            {loading && (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <span>Loading ripples...</span>
              </div>
            )}

            {!loading && hasMore && (
              <Button variant="outline" onClick={() => fetchAppreciations(page + 1)} fullWidth>
                Load More
              </Button>
            )}

            {!loading && appreciations.length === 0 && (
              <div className={styles.empty}>
                <Heart size={48} />
                <h3>No ripples yet</h3>
                <p>Be the first to recognize a colleague!</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className={styles.sidebar}>
          {/* Top Recognized */}
          <div className={styles.sidebarCard}>
            <h3>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2"><path d="M12 2l2 7h7l-5.5 4 2 7L12 16l-5.5 4 2-7L3 9h7l2-7z"/></svg>
              Top Recognized
            </h3>
            {leaderboard.map((p, i) => (
              <div key={p.name} className={styles.leaderItem}>
                <span className={`${styles.leaderRank} ${i === 0 ? styles.rank1 : i === 1 ? styles.rank2 : i === 2 ? styles.rank3 : styles.rankOther}`}>
                  {i + 1}
                </span>
                <Avatar src={p.avatar_url} name={p.name} size="sm" />
                <span className={styles.leaderName}>{p.name}</span>
                <span className={styles.leaderWaves}>
                  <Waves size={14} /> {p.waves}
                </span>
              </div>
            ))}
            {leaderboard.length === 0 && (
              <p style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', padding: '12px 0' }}>No data yet</p>
            )}
          </div>

          {/* Recognition Types */}
          <div className={styles.sidebarCard}>
            <h3>Recognition Types</h3>
            {breakdown.map(b => (
              <div key={b.key} className={styles.breakdownItem}>
                <div className={styles.breakdownHeader}>
                  <span className={styles.breakdownLabel}>{b.emoji} {b.label}</span>
                  <span className={styles.breakdownCount}>{b.count}</span>
                </div>
                <div className={styles.breakdownBar}>
                  <div className={styles.breakdownFill} style={{ width: `${b.pct}%`, background: `linear-gradient(90deg, ${b.color}, ${b.color}88)` }} />
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModalOpen} onClose={() => { setDeleteModalOpen(false); setDeleteId(null); }} title="Delete Appreciation">
        <div className={styles.deleteModal}>
          <p>Are you sure you want to delete this appreciation?</p>
          <p className={styles.deleteWarning}>
            This will also reverse the points: the recipient will lose the points and they will be restored to the sender.
          </p>
          <div className={styles.deleteActions}>
            <Button variant="ghost" onClick={() => { setDeleteModalOpen(false); setDeleteId(null); }}>Cancel</Button>
            <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Ripple Modal */}
      <Modal isOpen={editModalOpen} onClose={() => { setEditModalOpen(false); setEditingAppreciation(null); }} title="Edit Ripple">
        <div className={styles.editModal}>
          <div className={styles.editField}>
            <label>Message</label>
            <MentionInput
              mode="textarea"
              value={editForm.message}
              onChange={(val) => setEditForm({ ...editForm, message: val })}
              placeholder="Write your message... Use @ to mention"
              rows={4}
            />
          </div>
          <div className={styles.editField}>
            <label>Ripple Type</label>
            <div className={styles.editTypeGrid}>
              {Object.entries(RIPPLE_TYPES).map(([key, { label, emoji }]) => (
                <button key={key} className={`${styles.editTypeBtn} ${editForm.type === key ? styles.selected : ''}`} onClick={() => setEditForm({ ...editForm, type: key })}>
                  {emoji} {label}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.editField}>
            <label>Visibility</label>
            <div className={styles.editVisibilityToggle}>
              <button className={`${styles.editVisibilityBtn} ${editForm.is_public ? styles.selected : ''}`} onClick={() => setEditForm({ ...editForm, is_public: true })}>Public</button>
              <button className={`${styles.editVisibilityBtn} ${!editForm.is_public ? styles.selected : ''}`} onClick={() => setEditForm({ ...editForm, is_public: false })}>Private</button>
            </div>
          </div>
          <div className={styles.editField}>
            <label>Media</label>
            <input type="file" ref={editFileInputRef} accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip" multiple onChange={handleEditMediaSelect} style={{ display: 'none' }} />
            {editForm.media_files.length > 0 ? (
              <div className={styles.editMediaPreview}>
                {editForm.media_files.map((mf, i) => (
                  <div key={i} style={{ position: 'relative', display: 'inline-block', marginRight: 8 }}>
                    {mf.type === 'image' ? (
                      <img src={mf.url} alt="Preview" className={styles.editPreviewImage} style={{ maxWidth: 120, maxHeight: 90, borderRadius: 8 }} />
                    ) : mf.type === 'video' ? (
                      <video src={mf.url} className={styles.editPreviewVideo} style={{ maxWidth: 120, maxHeight: 90, borderRadius: 8 }} controls />
                    ) : (
                      <span style={{ display: 'inline-block', padding: '8px 12px', background: '#f7f5f0', borderRadius: 8, fontSize: 12 }}>{mf.name || 'File'}</span>
                    )}
                    <button className={styles.removeEditMedia} onClick={() => handleRemoveEditMedia(i)} style={{ position: 'absolute', top: -6, right: -6 }}><X size={14} /></button>
                  </div>
                ))}
              </div>
            ) : null}
            {editForm.media_files.length < 5 && (
              <div className={styles.editMediaUpload}>
                <button className={styles.editUploadButton} onClick={() => editFileInputRef.current?.click()}>
                  <Image size={20} /><span>{editForm.media_files.length > 0 ? 'Add more' : 'Upload media'}</span>
                </button>
              </div>
            )}
            <p className={styles.editMediaHint}>Max file size: 5MB · Up to 5 files</p>
          </div>
          {companyValues.length > 0 && (
            <div className={styles.editField}>
              <label>Company Value (Optional)</label>
              <div className={styles.editValueGrid}>
                {companyValues.map((value) => (
                  <button
                    key={value.id}
                    className={`${styles.editValueBtn} ${editForm.company_value_id === value.id ? styles.selected : ''}`}
                    onClick={() => setEditForm({ ...editForm, company_value_id: editForm.company_value_id === value.id ? null : value.id })}
                    style={{ '--value-color': value.color } as any}
                  >
                    {renderValueIcon(value.icon, 14, value.color)} {value.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className={styles.editActions}>
            <Button variant="ghost" onClick={() => { setEditModalOpen(false); setEditingAppreciation(null); }}>Cancel</Button>
            <Button onClick={handleEditSave} disabled={saving || !editForm.message.trim() || editForm.message.length < 10}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Viewed By Modal */}
      <Modal isOpen={showViewersModal} onClose={() => setShowViewersModal(false)} title="Viewed By">
        <div className={styles.viewersModal}>
          <p className={styles.viewersSubtitle}>
            <Eye size={14} /> {viewersData.length} {viewersData.length === 1 ? 'person' : 'people'} viewed {viewersTitle}
          </p>
          {viewersLoading ? (
            <div className={styles.viewersLoading}>Loading...</div>
          ) : viewersData.length === 0 ? (
            <div className={styles.viewersEmpty}>No one has viewed this yet.</div>
          ) : (
            <div className={styles.viewersList}>
              {viewersData.map((viewer: any, idx: number) => (
                <div key={idx} className={styles.viewerRow}>
                  <Avatar
                    src={viewer.avatar_url}
                    name={`${viewer.first_name} ${viewer.last_name}`}
                    size="sm"
                  />
                  <div className={styles.viewerInfo}>
                    <span className={styles.viewerName}>{viewer.first_name} {viewer.last_name}</span>
                  </div>
                  <span className={styles.viewerTime}>
                    {new Date(viewer.viewed_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                    {' '}
                    {new Date(viewer.viewed_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      <Modal isOpen={showReactionsModal} onClose={() => setShowReactionsModal(false)} title="Reactions">
        <div style={{ minWidth: 280 }}>
          <p style={{ fontSize: 13, color: 'rgba(20,32,29,0.5)', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            {reactionsModalData.length} {reactionsModalData.length === 1 ? 'person' : 'people'} reacted
          </p>
          {reactionsModalData.map((reactor: any, idx: number) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
              <Avatar src={reactor.avatar_url} name={`${reactor.first_name} ${reactor.last_name}`} size="sm" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#14201d' }}>{reactor.first_name} {reactor.last_name}</div>
              </div>
              <span style={{ fontSize: 16 }}>{reactor.emoji}</span>
            </div>
          ))}
        </div>
      </Modal>

      {previewImage && (
        <div className={styles.lightboxOverlay} onClick={() => setPreviewImage(null)}>
          <button className={styles.lightboxClose} onClick={() => setPreviewImage(null)}>&#10005;</button>
          <img src={previewImage} alt="Full size" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default Feed;
